import IWebPhoneService from '../IWebPhoneService';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import getPcsConversation from '../lib/getPcsConversation';
import { phoneState, phoneStateCallExists } from '../state';
import {
  CallAttributes,
  WebphoneConnectionStatus,
  WebphoneCallState,
  WebphonePhoneStateEvent,
} from '../types';

import IAppLogger from '@app/lib/IAppLogger';
import handleCallPlaced from './handleCallPlaced';
import handleCallStateUpdate from './handleCallStateUpdate';
import emitPcsConversationFetched from './emitPcsConversationFetched';
import { defaultRecordingState } from '../lib/getRecordingState';
import appManager from '@app/appManager';
import handleCheckWebPhoneRegistration from './handleCheckWebPhoneRegistration';
import { webPhoneLastTriggeringEventNames } from '../constants/events';
import { cacheCall } from '../lib/calls';
import getPcsConversationDetails from '@api/queries/getPcsConversationDetails';
import emitConversationDetailsFetched from './emitConversationDetailsFetched';
import { clearRecoveryState } from '../lib/clearRecoveryState';
import { resetAudioDevicesState } from '../audioDevicesState';

const handlePhoneStateUpdate = async (
  webPhoneService: IWebPhoneService,
  phoneStateUpdateEvent: WebphonePhoneStateEvent,
  logger: IAppLogger,
) => {
  if (!phoneState.phoneConnected && phoneStateUpdateEvent.phoneConnected) {
    // reset user locally triggered disconnect state as we reconnected
    phoneState.disconnectTriggeredLocally = false;
  }

  if (phoneState.phoneConnected && !phoneStateUpdateEvent.phoneConnected) {
    resetAudioDevicesState();
  }

  phoneState.phoneConnected = phoneStateUpdateEvent.phoneConnected;
  phoneState.registrationFailed = phoneStateUpdateEvent.registrationFailed;
  phoneState.connectionStatus = phoneStateUpdateEvent.connectionStatus;

  if (
    phoneState.isOverWritingRegistration &&
    phoneStateUpdateEvent.connectionStatus !==
      WebphoneConnectionStatus.Connecting
  ) {
    phoneState.isOverWritingRegistration = false;
  }

  // Handle connection recovery completion
  if (
    phoneState.isRecovering &&
    phoneStateUpdateEvent.connectionStatus ===
      WebphoneConnectionStatus.Connected
  ) {
    clearRecoveryState();
  }

  const { callId } = phoneStateUpdateEvent.callState;

  // since the current callState can be removed after syncing the callList, we need to keep track of the current value so that we can evaluate the change
  const currentCallStateIndex = phoneState.callList.findIndex(
    (call) => call.callId === callId,
  );

  let currentCallState: WebphoneCallState | undefined = undefined;
  if (currentCallStateIndex !== -1) {
    currentCallState = {
      ...phoneState.callList[currentCallStateIndex],
    };
  }

  // Cache call information before syncing callList
  if (currentCallState) {
    cacheCall({
      callId: currentCallState.callId,
      orientation: currentCallState.direction,
      timestamp: new Date().toISOString(),
    });
  }

  // sync the callList with the callList from the event
  phoneState.callList = phoneStateUpdateEvent.callList;

  // webphone call started
  if (
    phoneStateUpdateEvent.triggeringEvent ===
    webPhoneLastTriggeringEventNames.callStarted
  ) {
    const attributes: CallAttributes = {
      pcsConversationSid: '',
      queueIds: [],
      recordingState: defaultRecordingState,
      traits: [],
      tenantId: '',
    };

    getPcsConversation(phoneStateUpdateEvent.callState.remoteCallId)
      .then((pcsConversation) => {
        if (!pcsConversation) {
          logger.error(
            `PcsConversation not found for remoteCallId ${phoneStateUpdateEvent.callState.remoteCallId}`,
          );
          throw new Error('PcsConversation not found');
        }

        emitPcsConversationFetched(
          pcsConversation,
          phoneStateUpdateEvent,
          logger,
        );
      })
      .catch((error) => logger.error(error));

    // fetch detailed conversation information for incoming calls
    if (phoneStateUpdateEvent.callState.direction === 'terminating') {
      if (
        FeatureFlagModule.isEnabled(
          FeatureFlagKeys.featureInboxIncomingTransferTag,
        )
      ) {
        getPcsConversationDetails(phoneStateUpdateEvent.callState.remoteCallId)
          .then((result) => {
            emitConversationDetailsFetched(
              phoneStateUpdateEvent.callState.callId,
              result,
              logger,
            );
          })
          .catch((error) => {
            logger.error('Could not get transfer details', { error });
          });
      }
    }

    handleCallPlaced(
      webPhoneService,
      { ...phoneStateUpdateEvent.callState, ...attributes },
      logger,
    );
  } else if (phoneStateUpdateEvent.triggeringEvent && currentCallState) {
    // handle call state update
    handleCallStateUpdate(
      webPhoneService,
      phoneStateUpdateEvent.callState,
      currentCallState,
      logger,
    );
  }

  // remove the call attributes for the call if they are no longer in the callList
  if (
    phoneStateUpdateEvent.triggeringEvent ===
    webPhoneLastTriggeringEventNames.callEnded
  ) {
    const attributeCallIds = Object.keys(phoneState.callAttributes);
    attributeCallIds.forEach((callId) => {
      if (!phoneStateCallExists(callId)) {
        delete phoneState.callAttributes[callId];
      }
    });
    handleCheckWebPhoneRegistration();
  }

  if (
    phoneStateUpdateEvent.triggeringEvent ===
    webPhoneLastTriggeringEventNames.userAgentRegistered
  ) {
    phoneState.isOverWritingRegistration = false;
    appManager.events.emit('phone.registered');
  }

  // when answer a new call, the existing call is put on hold automatically
  // and demoted event is emitted with `held` set to true.
  const isAutoHold =
    phoneStateUpdateEvent.triggeringEvent ===
      webPhoneLastTriggeringEventNames.callDemoted &&
    !!currentCallState &&
    !currentCallState.held &&
    phoneStateUpdateEvent.callState.held;

  if (
    phoneStateUpdateEvent.triggeringEvent ===
      webPhoneLastTriggeringEventNames.callHoldStarted ||
    isAutoHold
  ) {
    appManager.events.emit(
      'phone.call.hold.started',
      phoneStateUpdateEvent.callState,
    );
  } else if (
    phoneStateUpdateEvent.triggeringEvent ===
    webPhoneLastTriggeringEventNames.callHoldEnded
  ) {
    appManager.events.emit(
      'phone.call.hold.ended',
      phoneStateUpdateEvent.callState,
    );
  }

  // handle the connection status
  if (
    phoneStateUpdateEvent.connectionStatus ===
    WebphoneConnectionStatus.WaitingToConnect
  ) {
    phoneState.existingRegistrationsDetected = true;
  } else {
    phoneState.existingRegistrationsDetected = false;
  }
};

export default handlePhoneStateUpdate;
